import { useState, useEffect } from "react";

// ============================================================
// WOLF LOGO — Reusable brand logo with cyan glow B+ animation
// Same component as UserDashboard for consistency.
// ============================================================
function WolfLogo({ size = 72, style = {} }) {
  // Inject keyframes once globally on first render (idempotent)
  useEffect(() => {
    if (document.getElementById("wolf-pulse-keyframes")) return;
    const styleTag = document.createElement("style");
    styleTag.id = "wolf-pulse-keyframes";
    styleTag.innerHTML = `
      @keyframes wolfPulse {
        0%, 100% {
          filter: drop-shadow(0 0 4px rgba(34, 211, 238, 0.75)) drop-shadow(0 0 12px rgba(34, 211, 238, 0.32));
        }
        50% {
          filter: drop-shadow(0 0 6px rgba(34, 211, 238, 1)) drop-shadow(0 0 18px rgba(34, 211, 238, 0.6));
        }
      }
    `;
    document.head.appendChild(styleTag);
  }, []);

  return (
    <img
      src="/wolf-logo.png"
      alt="LASKY"
      style={{
        width: size,
        height: "auto",
        animation: "wolfPulse 2.4s ease-in-out infinite",
        ...style,
      }}
    />
  );
}

// ============================================================
// LASKY SL/TP SYNC — Coming Soon / Maintenance Page
// © 2026 Shadow Technologies. All Rights Reserved.
//
// Shown to public users while system is in maintenance mode.
// Admins bypass via /admin?bypass=Lasky
// ============================================================

export default function ComingSoonPage() {
  const [dots, setDots] = useState("");

  // Animated dots for "Launching soon..." effect
  useEffect(() => {
    const iv = setInterval(() => {
      setDots(d => (d.length >= 3 ? "" : d + "."));
    }, 500);
    return () => clearInterval(iv);
  }, []);

  return (
    <div style={{
      minHeight: "100vh",
      background: "#06060B",
      color: "#F1F5F9",
      fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px",
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Grid background */}
      <div style={{
        position: "fixed",
        inset: 0,
        opacity: 0.04,
        backgroundImage: `
          linear-gradient(#22D3EE 1px, transparent 1px),
          linear-gradient(90deg, #22D3EE 1px, transparent 1px)
        `,
        backgroundSize: "48px 48px",
        pointerEvents: "none",
      }} />

      {/* Radial glow */}
      <div style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "800px",
        height: "800px",
        background: "radial-gradient(circle, rgba(34,211,238,0.08) 0%, transparent 60%)",
        pointerEvents: "none",
      }} />

      {/* Main card */}
      <div style={{
        position: "relative",
        maxWidth: 520,
        width: "100%",
        padding: "48px 40px",
        background: "linear-gradient(135deg, #0D0D14 0%, #0A0A10 100%)",
        border: "1px solid #1E293B",
        borderRadius: 16,
        textAlign: "center",
        boxShadow: "0 20px 60px rgba(0,0,0,0.5), 0 0 40px rgba(34,211,238,0.08)",
      }}>
        {/* Logo / Icon */}
        <div style={{
          display: "flex",
          justifyContent: "center",
          marginBottom: 24,
        }}>
          <WolfLogo size={72} />
        </div>

        {/* Brand */}
        <h1 style={{
          fontSize: 24,
          fontWeight: 800,
          letterSpacing: "-0.02em",
          margin: "0 0 8px 0",
          color: "#F1F5F9",
        }}>
          LASKY SL/TP SYNC
        </h1>

        <div style={{
          fontSize: 11,
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          color: "#22D3EE",
          fontWeight: 600,
          marginBottom: 32,
        }}>
          MT5 Cloud Sync Engine
        </div>

        {/* Divider */}
        <div style={{
          height: 1,
          background: "linear-gradient(90deg, transparent, #1E293B, transparent)",
          margin: "0 -20px 32px",
        }} />

        {/* Status */}
        <div style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 14px",
          borderRadius: 20,
          background: "#F59E0B10",
          border: "1px solid #F59E0B30",
          marginBottom: 20,
        }}>
          <span style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "#F59E0B",
            animation: "pulse 2s ease-in-out infinite",
          }} />
          <span style={{ color: "#F59E0B", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase" }}>
            Under Development
          </span>
        </div>

        {/* Main headline */}
        <h2 style={{
          fontSize: 32,
          fontWeight: 800,
          letterSpacing: "-0.03em",
          margin: "0 0 16px 0",
          color: "#F1F5F9",
          lineHeight: 1.1,
        }}>
          Coming Soon{dots}
        </h2>

        {/* Description */}
        <p style={{
          fontSize: 14,
          lineHeight: 1.7,
          color: "#94A3B8",
          margin: "0 0 32px 0",
          maxWidth: 400,
          marginLeft: "auto",
          marginRight: "auto",
        }}>
          A cloud-powered stop-loss and take-profit sync engine for MT5 traders.
          One change, all positions — even when your terminal is offline.
        </p>

        {/* Features preview */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 12,
          marginBottom: 32,
        }}>
          {[
            { icon: "⚡", label: "Real-time" },
            { icon: "☁️", label: "Cloud 24/7" },
            { icon: "🔒", label: "Encrypted" },
          ].map((f, i) => (
            <div key={i} style={{
              padding: "14px 8px",
              background: "#0A0A10",
              border: "1px solid #1E293B",
              borderRadius: 8,
            }}>
              <div style={{ fontSize: 18, marginBottom: 4 }}>{f.icon}</div>
              <div style={{ fontSize: 10, color: "#64748B", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                {f.label}
              </div>
            </div>
          ))}
        </div>

        {/* Contact */}
        <div style={{
          padding: "16px 20px",
          background: "#22D3EE08",
          border: "1px solid #22D3EE20",
          borderRadius: 10,
          marginBottom: 24,
        }}>
          <div style={{ fontSize: 11, color: "#64748B", fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 6 }}>
            Stay Updated
          </div>
          <a
            href="mailto:laskysystem@gmail.com"
            style={{
              color: "#22D3EE",
              fontSize: 14,
              fontWeight: 600,
              textDecoration: "none",
              letterSpacing: "-0.01em",
            }}
          >
            laskysystem@gmail.com
          </a>
        </div>

        {/* Footer */}
        <div style={{
          fontSize: 11,
          color: "#475569",
          letterSpacing: "0.05em",
        }}>
          © 2026 Shadow Technologies · SSM 202603100162
        </div>
      </div>

      {/* Pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.85); }
        }
      `}</style>
    </div>
  );
}
